import { collectPredecessorsOrSuccessors } from "@/components/GraphVisualisation/hooks/useGraphVisualisation";
import { graphlib } from "@dagrejs/dagre";
import { ensureParsedGraph } from "@imminently/decisively-core";
import Select from "@imminently/decisively-ui/Select";
import React from "react";
import { useGraphVisualisation } from "../../../hooks/useGraphVisualisation";

interface DepthSelectorProps {}

const DepthSelector = (props: DepthSelectorProps) => {
  const {
    graph,
    restrictViewToDepth,
    restrictedView,
  } = useGraphVisualisation();
  const restrictedViewRootPath = restrictedView?.rootPath;

  const maxDepthFromRestrictedViewRootPath = React.useMemo(() => {
    let maxDepthFromFocus: number | null = null;
    const isShowHidden = true;

    if (restrictedViewRootPath && graph) {
      let parsedGraph = graph;
      if (!parsedGraph._nodes) {
        parsedGraph = ensureParsedGraph(graph);
      }

      const maxExploreDepth = 10;
      const allRelevantNodes = new Set([
        restrictedViewRootPath,
        ...collectPredecessorsOrSuccessors(parsedGraph, restrictedViewRootPath, maxExploreDepth, "predecessors"),
        ...collectPredecessorsOrSuccessors(parsedGraph, restrictedViewRootPath, maxExploreDepth, "successors"),
      ]);

      const displayGraph = new graphlib.Graph();
      parsedGraph.nodes().forEach((id) => {
        const node = parsedGraph.node(id);
        if (!node || !allRelevantNodes.has(id) || (!isShowHidden && node.hidden) || !node.description) {
          return;
        }
        displayGraph.setNode(id, node?.description || "-");
      });
      parsedGraph.edges().forEach((edge) => {
        const node1: any = parsedGraph.node(edge.v);
        const node2: any = parsedGraph.node(edge.w);
        if (
          !node1 ||
          !node2 ||
          !allRelevantNodes.has(edge.v) ||
          !allRelevantNodes.has(edge.w) ||
          (!isShowHidden && (node1.hidden || node2.hidden))
        ) {
          return;
        }
        displayGraph.setEdge(edge.v, edge.w, {});
      });

      if (displayGraph.node(restrictedViewRootPath)) {
        const dj = graphlib.alg.dijkstra(displayGraph, restrictedViewRootPath, undefined, (v) =>
          (displayGraph as any).nodeEdges(v),
        );
        let maxDepthFromFocusInner = 0;
        Object.keys(dj).forEach((w) => {
          if (dj[w].distance !== Number.POSITIVE_INFINITY) {
            const reachableNode = parsedGraph.node(w);
            const isHidden = reachableNode.hidden ?? false;
            if (!isHidden || isShowHidden) {
              maxDepthFromFocusInner = Math.max(maxDepthFromFocusInner, dj[w].distance);
            }
          }
        });
        maxDepthFromFocus = maxDepthFromFocusInner;
      }
    }

    return maxDepthFromFocus;
  }, [graph, restrictedViewRootPath]);

  React.useEffect(() => {
    const restrictedViewDepth = restrictedView?.graphDepth ?? null;
    if (
      restrictedViewDepth !== null &&
      maxDepthFromRestrictedViewRootPath !== null &&
      restrictedViewDepth > maxDepthFromRestrictedViewRootPath
    ) {
      restrictViewToDepth(maxDepthFromRestrictedViewRootPath);
    } else if (maxDepthFromRestrictedViewRootPath === null && restrictedViewDepth !== null) {
      restrictViewToDepth(null);
    } else if (restrictedViewDepth === null && maxDepthFromRestrictedViewRootPath !== null) {
      if (maxDepthFromRestrictedViewRootPath > 0) {
        restrictViewToDepth(1);
      } else if (maxDepthFromRestrictedViewRootPath === 0) {
        restrictViewToDepth(0);
      }
    }
  }, [maxDepthFromRestrictedViewRootPath, restrictedView?.graphDepth, restrictViewToDepth]);

  const options = React.useMemo(() => {
    const opts = maxDepthFromRestrictedViewRootPath
      ? Array.from(Array(maxDepthFromRestrictedViewRootPath).keys()).map((i) => ({
          value: (++i).toString(),
          label: i.toString(),
        }))
      : [];
    opts.unshift({ value: "all", label: "All" });
    return opts;
  }, [maxDepthFromRestrictedViewRootPath]);

  const isDisabled =
    !(maxDepthFromRestrictedViewRootPath !== null && maxDepthFromRestrictedViewRootPath >= 0) ||
    maxDepthFromRestrictedViewRootPath === 0;

  const currentValue = React.useMemo(() => {
    const restrictedViewDepth = restrictedView?.graphDepth;
    if (!restrictedViewDepth) {
      return "all";
    }
    return restrictedViewDepth.toString();
  }, [restrictedView?.graphDepth]);

  const handleChange = React.useCallback(
    (value: string | null) => {
      if (value === "all") {
        restrictViewToDepth(null);
      } else {
        restrictViewToDepth(Number(value));
      }
    },
    [restrictViewToDepth],
  );

  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="whitespace-nowrap text-muted-foreground">Max depth</span>
      <Select
        value={currentValue}
        options={options}
        onValueChange={handleChange}
        disabled={isDisabled}
        className="min-w-[8rem]"
      />
    </label>
  );
};

export default DepthSelector;
